import { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Chip, FormControl, InputLabel, Select, MenuItem, Grid
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Add } from '@mui/icons-material';
import { useSerialStore } from '../store/serialStore';
import { serialService } from '../services/serialService';
import { type ArticuloSerial, initialSerialState } from '../types/serial.types';

const estadoLabels: Record<string, { label: string; color: 'success' | 'warning' | 'error' }> = {
  DIS: { label: 'Disponible', color: 'success' },
  ASI: { label: 'Asignado', color: 'warning' },
  SAL: { label: 'Salida', color: 'error' },
};

const SerialesPage = () => {
  const { seriales, loading, setSeriales, setLoading } = useSerialStore();
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState<ArticuloSerial>(initialSerialState);
  const [filtroEstado, setFiltroEstado] = useState('');

  useEffect(() => { loadData(); }, [filtroEstado]);

  const loadData = async () => {
    setLoading(true);
    try {
      setSeriales(await serialService.getAll(undefined, undefined, filtroEstado || undefined));
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      await serialService.save(formData);
      setOpenDialog(false);
      await loadData();
    } catch { /* handled */ }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Eliminar este serial?')) return;
    try { await serialService.delete(id); await loadData(); } catch { /* handled */ }
  };

  const columns: GridColDef[] = [
    { field: 'numeroSerial', headerName: 'N° Serie', width: 150 },
    { field: 'articuloCodigo', headerName: 'Código Art.', width: 120 },
    { field: 'articuloDescripcion', headerName: 'Artículo', flex: 1, minWidth: 200 },
    { field: 'depositoNombre', headerName: 'Almacén', width: 150 },
    { field: 'loteNumero', headerName: 'Lote', width: 120 },
    { field: 'estado', headerName: 'Estado', width: 120,
      renderCell: (params) => {
        const info = estadoLabels[params.value] || { label: params.value, color: 'default' as const };
        return <Chip label={info.label} size="small" color={info.color} />;
      }
    },
    { field: 'fechaIngreso', headerName: 'Ingreso', width: 120,
      valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : '-' },
    { field: 'actions', headerName: 'Acciones', width: 100, sortable: false,
      renderCell: (params) => (
        <Button size="small" color="error" onClick={() => handleDelete(params.row.idArticuloSerial)}>Eliminar</Button>
      )
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Números de Serie</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setFormData(initialSerialState); setOpenDialog(true); }}>
          Nuevo Serial
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Estado</InputLabel>
          <Select value={filtroEstado} label="Estado" onChange={(e) => setFiltroEstado(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="DIS">Disponible</MenuItem>
            <MenuItem value="ASI">Asignado</MenuItem>
            <MenuItem value="SAL">Salida</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Paper sx={{ height: 500 }}>
        <DataGrid rows={seriales} columns={columns} getRowId={(row) => row.idArticuloSerial}
          loading={loading} density="compact"
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo Serial</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Número de Serie" value={formData.numeroSerial}
                onChange={(e) => setFormData({ ...formData, numeroSerial: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="ID Artículo" type="number" value={formData.idArticulo || ''}
                onChange={(e) => setFormData({ ...formData, idArticulo: Number(e.target.value) })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="ID Almacén" type="number" value={formData.idDeposito ?? ''}
                onChange={(e) => setFormData({ ...formData, idDeposito: Number(e.target.value) || null })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="ID Lote" type="number" value={formData.idLote ?? ''}
                onChange={(e) => setFormData({ ...formData, idLote: Number(e.target.value) || null })} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Observaciones" multiline rows={2}
                value={formData.observaciones ?? ''}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SerialesPage;
